// FILE: src/pages/TerritoryReport.jsx

import { useState } from "react";
import { useFetch } from "../hooks/useFetch";
import { getTerritoryReportByAdmin } from "../api/subscriptionsApi";
import AccessControl from "../components/AccessControl";
import "./css/TerritoryReport.css";

// ─── Status pill ──────────────────────────────────────────────────────────────
function StatusPill({ status, method, depositedToBank, bankVerified }) {
  if (status === "pending")
    return <span className="tr-pill tr-pill-pending">⏳ Pending</span>;
  if (status === "rejected")
    return <span className="tr-pill tr-pill-rejected">🚫 Rejected</span>;
  if (status === "paid") {
    if (method === "online")
      return <span className="tr-pill tr-pill-online">✅ Online</span>;
    if (bankVerified)
      return <span className="tr-pill tr-pill-verified">✅ Verified</span>;
    if (depositedToBank)
      return <span className="tr-pill tr-pill-deposited">🏦 Deposited</span>;
    return <span className="tr-pill tr-pill-cash">💵 Cash Collected</span>;
  }
  return <span className="tr-pill">{status}</span>;
}

// ─── Territory card ───────────────────────────────────────────────────────────
function TerritoryCard({ group }) {
  const [open, setOpen] = useState(false);

  const cashNotDeposited = group.drivers.filter(
    (d) => d.method === "cash" && d.status === "paid" && !d.depositedToBank,
  );
  const isEmpty = group.drivers.length === 0;

  return (
    <div
      className={[
        "tr-card",
        cashNotDeposited.length > 0 ? "tr-card-warn" : "",
        isEmpty ? "tr-card-empty" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Header */}
      <div
        className="tr-card-header"
        onClick={() => !isEmpty && setOpen((o) => !o)}
        style={{ cursor: isEmpty ? "default" : "pointer" }}
      >
        <div className="tr-card-left">
          <div className="tr-zone-info">
            <span className="tr-territory-name">{group.zoneName}</span>
            {(group.city || group.state) && (
              <span className="tr-zone-location">
                📍 {[group.city, group.state].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
          {cashNotDeposited.length > 0 && (
            <span className="tr-warn-badge">
              ⚠ {cashNotDeposited.length} not deposited
            </span>
          )}
          {isEmpty && (
            <span className="tr-zero-badge">No payments today</span>
          )}
        </div>

        <div className="tr-card-right">
          {!isEmpty && (
            <>
              <div className="tr-stat">
                <span className="tr-stat-label">Collected</span>
                <span className="tr-stat-value tr-green">
                  ₹{group.totalAmount.toLocaleString()}
                </span>
              </div>
              <div className="tr-stat-divider" />
              <div className="tr-stat">
                <span className="tr-stat-label">Cash</span>
                <span className="tr-stat-value">
                  ₹{group.cashAmount.toLocaleString()}
                </span>
              </div>
              <div className="tr-stat-divider" />
              <div className="tr-stat">
                <span className="tr-stat-label">Online</span>
                <span className="tr-stat-value">
                  ₹{group.onlineAmount.toLocaleString()}
                </span>
              </div>
              <div className="tr-stat-divider" />
              <div className="tr-stat">
                <span className="tr-stat-label">Drivers</span>
                <span className="tr-stat-value">{group.drivers.length}</span>
              </div>
              {group.pendingCount > 0 && (
                <>
                  <div className="tr-stat-divider" />
                  <div className="tr-stat">
                    <span className="tr-stat-label">Pending</span>
                    <span className="tr-stat-value tr-orange">
                      {group.pendingCount}
                    </span>
                  </div>
                </>
              )}
              <span className="tr-expand-icon">{open ? "▲" : "▼"}</span>
            </>
          )}
        </div>
      </div>

      {/* Driver rows */}
      {open && !isEmpty && (
        <div className="tr-driver-table-wrap">
          <table className="tr-driver-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Driver</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Collected By</th>
                <th>Type</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {group.drivers.map((d, i) => (
                <tr
                  key={d.paymentId}
                  className={
                    d.status === "pending"
                      ? "tr-row-pending"
                      : d.method === "cash" &&
                          d.status === "paid" &&
                          !d.depositedToBank
                        ? "tr-row-warn"
                        : ""
                  }
                >
                  <td className="tr-idx">{i + 1}</td>
                  <td>
                    <p className="tr-driver-name">{d.driverName}</p>
                    <p className="tr-driver-phone">{d.driverPhone}</p>
                  </td>
                  <td>
                    <span className="tr-plan-badge">{d.planName}</span>
                  </td>
                  <td>
                    <span className="tr-amount">
                      ₹{d.amount?.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`tr-method ${
                        d.method === "online"
                          ? "tr-method-online"
                          : "tr-method-cash"
                      }`}
                    >
                      {d.method === "online" ? "🌐 Online" : "💵 Cash"}
                    </span>
                  </td>
                  <td>
                    <StatusPill
                      status={d.status}
                      method={d.method}
                      depositedToBank={d.depositedToBank}
                      bankVerified={d.bankVerified}
                    />
                  </td>
                  <td>
                    {d.collectedBy || (
                      <span className="tr-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`tr-type-badge ${
                        d.isRenewal ? "tr-type-renew" : "tr-type-new"
                      }`}
                    >
                      {d.isRenewal ? "Renewal" : "New"}
                    </span>
                  </td>
                  <td className="tr-time">
                    {new Date(d.paidAt || d.createdAt).toLocaleTimeString(
                      "en-IN",
                      { hour: "2-digit", minute: "2-digit" },
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TerritoryReport() {
  const today = new Date().toISOString().split("T")[0];
  const [date,   setDate]   = useState(today);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "active" | "warn"

  const { data, loading, error, refetch } = useFetch(
    () => getTerritoryReportByAdmin({ date }),
    [date],
  );

  const territories = data?.territories ?? [];
  const summary     = data?.summary     ?? {};

  // ── Filters ───────────────────────────────────────────────────────────────
  let filtered = territories;

  if (search.trim()) {
    filtered = filtered.filter((t) =>
      t.zoneName.toLowerCase().includes(search.trim().toLowerCase()) ||
      t.city?.toLowerCase().includes(search.trim().toLowerCase()),
    );
  }

  if (filter === "active") {
    filtered = filtered.filter(
      (t) => t.paidCount > 0 || t.pendingCount > 0,
    );
  }
  if (filter === "warn") {
    filtered = filtered.filter((t) =>
      t.drivers.some(
        (d) => d.method === "cash" && d.status === "paid" && !d.depositedToBank,
      ),
    );
  }

  return (
    <div className="tr-container">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "ACCOUNTS"]}
        redirectTo="/404"
      />

      {/* Header */}
      <div className="tr-header">
        <div>
          <h1>Territory Report</h1>
          <p>
            Daily driver payments grouped by territory zone — cross-check cash
            vs records
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="tr-controls">
        <div className="tr-control-group">
          <label className="tr-control-label">Date</label>
          <input
            type="date"
            className="tr-date-input"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="tr-control-group">
          <label className="tr-control-label">Search Zone</label>
          <input
            type="text"
            className="tr-search-input"
            placeholder="Zone name or city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="tr-control-group">
          <label className="tr-control-label">Show</label>
          <div className="tr-filter-pills">
            {[
              { key: "all",    label: "All Zones" },
              { key: "active", label: "Active Only" },
              { key: "warn",   label: "⚠ Not Deposited" },
            ].map((f) => (
              <button
                key={f.key}
                className={`tr-filter-pill ${filter === f.key ? "active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <button className="tr-refresh-btn" onClick={refetch}>
          ↺ Refresh
        </button>
      </div>

      {/* Summary bar */}
      {!loading && !error && data && (
        <div className="tr-summary-bar">
          <div className="tr-summary-item">
            <span className="tr-summary-label">Grand Total</span>
            <span className="tr-summary-value tr-green">
              ₹{summary.totalAmount?.toLocaleString()}
            </span>
          </div>
          <div className="tr-summary-divider" />
          <div className="tr-summary-item">
            <span className="tr-summary-label">Cash</span>
            <span className="tr-summary-value">
              ₹{summary.cashAmount?.toLocaleString()}
            </span>
          </div>
          <div className="tr-summary-divider" />
          <div className="tr-summary-item">
            <span className="tr-summary-label">Online</span>
            <span className="tr-summary-value">
              ₹{summary.onlineAmount?.toLocaleString()}
            </span>
          </div>
          <div className="tr-summary-divider" />
          <div className="tr-summary-item">
            <span className="tr-summary-label">Paid</span>
            <span className="tr-summary-value">{summary.totalPaid}</span>
          </div>
          <div className="tr-summary-divider" />
          <div className="tr-summary-item">
            <span className="tr-summary-label">Pending</span>
            <span
              className={`tr-summary-value ${
                summary.totalPending > 0 ? "tr-orange" : ""
              }`}
            >
              {summary.totalPending}
            </span>
          </div>
          <div className="tr-summary-divider" />
          <div className="tr-summary-item">
            <span className="tr-summary-label">Zones Active</span>
            <span className="tr-summary-value">
              {summary.activeCount} / {summary.territoryCount}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="tr-skeleton">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="tr-skeleton-card" />
          ))}
        </div>
      ) : error ? (
        <p className="tr-error">{error}</p>
      ) : filtered.length === 0 ? (
        <div className="tr-empty">
          <p className="tr-empty-icon">📊</p>
          <p className="tr-empty-title">No territories found</p>
          <p className="tr-empty-sub">
            {search
              ? `"${search}" பொருந்துற zone இல்ல`
              : filter === "warn"
                ? "All cash payments have been deposited ✅"
                : "No payments on this date"}
          </p>
        </div>
      ) : (
        <div className="tr-cards-list">
          {filtered.map((group) => (
            <TerritoryCard key={group.zoneId ?? "unassigned"} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}